import { Box, FormControl, Input, Table } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import { useRole } from "../Contexts/RoleContext";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WrappedButton from "../WrappedButton";
import SignatureDialog from "../Dialogs/SignatureDialog";

export interface ProposalInvoiceItem {
	InvoiceNumber: string;
	CustomerName: string;
	InvoiceDate: string;
	TotalAmount: number;
	Description: string;
	InvoiceHeaderID: string;
	Status: string;
	IsApproved: boolean;
}

interface ProposalInvoiceGridProps {
	buildingId: string;
	isInvoice: boolean;
}

/**
 * Shared grid component for displaying estimates (proposals) and invoices for a specific building
 */
type SortField = "number" | "date" | "amount";
type SortDirection = "asc" | "desc";

export default function ProposalInvoiceGrid(props: ProposalInvoiceGridProps) {
	const [data, setData] = React.useState<Array<ProposalInvoiceItem>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [loadingPdfId, setLoadingPdfId] = React.useState<string | null>(null);
	const [loadingPaymentId, setLoadingPaymentId] = React.useState<string | null>(null);
	const [sortField, setSortField] = React.useState<SortField>("date");
	const [sortDirection, setSortDirection] = React.useState<SortDirection>("desc");
	// State for signature dialog
	const [signatureDialogOpen, setSignatureDialogOpen] = React.useState(false);
	const [selectedProposalId, setSelectedProposalId] = React.useState<string | null>(null);
	const [selectedEstimateNum, setSelectedEstimateNum] = React.useState<string | null>(null);
	const [currentFirstName, setCurrentFirstName] = React.useState("");
	const [currentLastName, setCurrentLastName] = React.useState("");
	const { effectiveRole } = useRole();

	React.useEffect(() => {
		getData();
		getUserInfo();
	}, [props.buildingId, props.isInvoice]);

	/**
	 * Fetches the current user's name for auto-populating the signature dialog
	 */
	const getUserInfo = () => {
		fetch("/User/GetPersonInfo", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((y) => {
				setCurrentFirstName(y.first_name)
				setCurrentLastName(y.last_name)
			});
	};

	/**
	 * Fetches proposals or invoices from the backend based on building ID and is_invoice flag
	 */
	const getData = () => {
		fetch("Proposal/GetProposalsInvoicesByBuilding", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				buildingsId: props.buildingId,
				isInvoice: props.isInvoice,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				setData(
					x.map((y: any) => {
						return {
							InvoiceNumber: y.invoice_number,
							CustomerName: y.building_name,
							InvoiceDate: y.invoice_date,
							TotalAmount: y.total_amount,
							Description: y.description,
							InvoiceHeaderID: y.invoice_header_id,
							Status: y.status,
							IsApproved: y.is_approved,
						} as ProposalInvoiceItem;
					})
				);
			});
	};

	/**
	 * Handles column header click to toggle sorting
	 */
	const handleSort = (field: SortField) => {
		if (sortField === field) {
			// Toggle direction if clicking the same field
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			// Set new field with default descending sort
			setSortField(field);
			setSortDirection("desc");
		}
		setPageNumber(0);
	};

	/**
	 * Renders sort indicator icon for column headers
	 */
	const renderSortIcon = (field: SortField) => {
		if (sortField !== field) return null;
		return sortDirection === "asc" ? (
			<ArrowUpwardIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }} />
		) : (
			<ArrowDownwardIcon sx={{ fontSize: 16, verticalAlign: "middle", ml: 0.5 }} />
		);
	};

	/**
	 * Opens the PDF for the selected proposal or invoice
	 */
	const openPdf = (item: ProposalInvoiceItem) => {
		setLoadingPdfId(item.InvoiceHeaderID);
		fetch("Proposal/GetPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNum: item.InvoiceNumber,
				isPackingList: false,
				invoiceHeaderId: item.InvoiceHeaderID,
				isInvoice: props.isInvoice,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			})
			.finally(() => {
				setLoadingPdfId(null);
			});
	};

	/**
	 * Opens the QBO payment link for an invoice in a new window
	 */
	const openPaymentLink = (item: ProposalInvoiceItem) => {
		setLoadingPaymentId(item.InvoiceHeaderID);
		fetch("Proposal/GetInvoicePaymentLink", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				invoiceNumber: item.InvoiceNumber,
			}),
		})
			.then((x) => x.json())
			.then((x) => {
				if (x.paymentLink) {
					window.open(x.paymentLink, "_blank");
				} else {
					console.error("No payment link returned");
				}
			})
			.catch((err) => {
				console.error("Error getting payment link:", err);
			})
			.finally(() => {
				setLoadingPaymentId(null);
			});
	};

	/**
	 * Opens the signature dialog for approving a proposal
	 */
	const openSignatureDialog = (proposalId: string, estimateNum: string) => {
		setSelectedProposalId(proposalId);
		setSelectedEstimateNum(estimateNum);
		setSignatureDialogOpen(true);
	};

	/**
	 * Handles successful signature approval - refreshes the data
	 */
	const handleSignatureApproved = () => {
		getData();
	};

	const filteredData = data?.filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.CustomerName.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber.toString().includes(globalFilter) ||
			x.Description?.toLowerCase().includes(globalFilter.toLowerCase())
	);

	// Sort the filtered data
	const sortedData = filteredData?.sort((a, b) => {
		let comparison = 0;
		switch (sortField) {
			case "number":
				comparison = a.InvoiceNumber.localeCompare(b.InvoiceNumber);
				break;
			case "date":
				comparison = new Date(a.InvoiceDate).getTime() - new Date(b.InvoiceDate).getTime();
				break;
			case "amount":
				comparison = a.TotalAmount - b.TotalAmount;
				break;
		}
		return sortDirection === "asc" ? comparison : -comparison;
	});

	const maxPages = Math.ceil((sortedData ?? []).length / 25);
	const thisRows = sortedData?.slice(pageNumber * 25, (pageNumber + 1) * 25);

	return (
		<>
			<Box sx={{ overflowY: "auto", padding: 2 }}>
				<div style={{ display: "flex", marginBottom: 2 }}>
					<FormControl sx={{ marginLeft: "auto" }}>
						<Input
							name="Search"
							placeholder="Global Search"
							onChange={(event) => {
								setGlobalFilter(event.target.value);
								setPageNumber(0);
							}}
							value={globalFilter}
						/>
					</FormControl>
				</div>
				<Table stripe={"odd"} hoverRow>
					<thead>
						<tr>
							<th style={{ cursor: "pointer" }} onClick={() => handleSort("number")}>
								{props.isInvoice ? "Invoice #" : "Estimate #"}
								{renderSortIcon("number")}
							</th>
							<th style={{ cursor: "pointer" }} onClick={() => handleSort("date")}>
								Date
								{renderSortIcon("date")}
							</th>
							<th style={{ cursor: "pointer" }} onClick={() => handleSort("amount")}>
								Amount
								{renderSortIcon("amount")}
							</th>
							<th>Description</th>
							<th>Status</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{thisRows?.map((x) => {
							return (
								<tr style={{ cursor: "pointer" }} key={x.InvoiceHeaderID}>
									<td>{x.InvoiceNumber}</td>
									<td>{new Date(x.InvoiceDate).toLocaleDateString()}</td>
									<td>${x.TotalAmount.toFixed(2)}</td>
									<td style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										{x.Description}
									</td>
									<td>{x.Status}</td>
									<td style={{ minWidth: "250px", whiteSpace: "nowrap" }}>
										<WrappedButton
											busy={loadingPdfId === x.InvoiceHeaderID}
											onClick={() => {
												openPdf(x);
											}}>
											Open PDF
										</WrappedButton>
										{/* Show Approve & Sign button for unapproved proposals (not invoices) */}
										{!props.isInvoice && !x.IsApproved && effectiveRole === "Admin" && x.Status?.toLowerCase() === "pending" && (
											<WrappedButton
												sx={{ ml: 1 }}
												color="success"
												onClick={() => openSignatureDialog(x.InvoiceHeaderID, x.InvoiceNumber)}>
												Approve
											</WrappedButton>
										)}
										{/* Show Add Payment button for unpaid invoices */}
										{props.isInvoice && x.Status?.toLowerCase() !== "paid" && (
											<WrappedButton
												sx={{ ml: 1 }}
												color="primary"
												busy={loadingPaymentId === x.InvoiceHeaderID}
												onClick={() => openPaymentLink(x)}>
												Pay
											</WrappedButton>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={6}>
								<div style={{ float: "right" }}>
									<span>
										<KeyboardArrowLeftIcon
											onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)}
											sx={{ verticalAlign: "middle", cursor: "pointer" }}
										/>{" "}
										{`Page ${pageNumber + 1} of ${maxPages}`}{" "}
										<KeyboardArrowRightIcon
											onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)}
											sx={{ verticalAlign: "middle", cursor: "pointer" }}
										/>
									</span>
								</div>
							</td>
						</tr>
					</tfoot>
				</Table>
			</Box>

			{/* Signature dialog for approving proposals */}
			{selectedProposalId && selectedEstimateNum && (
				<SignatureDialog
					proposalId={selectedProposalId}
					estimateNum={selectedEstimateNum}
					firstName={currentFirstName}
					lastName={currentLastName}
					open={signatureDialogOpen}
					onClose={() => setSignatureDialogOpen(false)}
					onApproved={handleSignatureApproved}
				/>
			)}
		</>
	);
}
